/**
 * AJAX form detection — captures email/phone from forms that submit via
 * JavaScript (Elementor, WPForms, Gravity Forms, CF7, HubSpot, etc.)
 * instead of firing the native submit event.
 *
 * Two layers:
 * 1. Submit button click detection — reads email synchronously at click time
 * 2. Input blur monitoring — captures email when user leaves the field
 */

import { state, debugLog } from "./state.js";
import { sendEvent } from "./event-transport.js";
import { captureEmail, capturePhone, captureNamePair, captureFullName } from "./pii-capture.js";
import { reinitPixelUserData } from "./facebook-pixel.js";
import { refreshPlatformPixelUserData } from "./platforms.js";
import { persistUserData } from "./user-data-store.js";
import {
  findEmailInput,
  findPhoneInput,
  getInputValue,
  findFormSubmitRule,
  wasNativelySubmitted,
  isNonConversionForm,
} from "./dom-listeners.js";
import { shouldSkipForRule } from "./conversion-dedup.js";
import {
  armPendingSubmission,
  hasPendingSubmission,
  installSubmissionSignalListeners,
} from "./pending-submission.js";

// ── Submit button selectors ────────────────────────────────────────
// Ordered: platform-specific first, generic last
const SUBMIT_SELECTOR = [
  // Native HTML
  'input[type="submit"]',
  'button[type="submit"]',
  // Elementor
  '.elementor-field-type-submit button',
  '.elementor-field-type-submit input',
  '.e-form__buttons__submit',
  // WPForms
  '.wpforms-submit',
  // Gravity Forms
  '.gform_button',
  '.gform_footer input[type="submit"]',
  // Contact Form 7
  '.wpcf7-submit',
  // HubSpot
  '.hs-button',
  '.hs-submit input',
  // Ninja Forms
  '.nf-field-type-submit input',
  // Formidable Forms
  '.frm_submit button',
  '.frm_submit input',
  // Generic: any button inside a form that isn't explicitly type="button" or "reset"
  'form button:not([type="button"]):not([type="reset"])',
].join(",");

// ── Email input selectors (for blur monitoring) ────────────────────
const EMAIL_INPUT_SELECTOR = [
  'input[type="email"]',
  'input[name*="email"]',
  'input[name*="e-mail"]',
  'input[id*="email"]',
  // Contact Form 7
  'input[name*="your-email"]',
  // WooCommerce
  'input[name="billing_email"]',
].join(",");

const PHONE_INPUT_SELECTOR = [
  'input[type="tel"]',
  'input[name*="phone"]',
  'input[name*="tel"]',
  // WooCommerce
  'input[name="billing_phone"]',
].join(",");

// ── Name input selectors ────────────────────────────────────────────
const FIRST_NAME_SELECTOR = [
  'input[name="first_name"]',
  'input[name="first-name"]',
  'input[name="firstname"]',
  'input[name*="fname"]',
  'input[name="given-name"]',
  'input[autocomplete="given-name"]',
  // Dutch forms
  'input[name*="voornaam"]',
  // WooCommerce
  'input[name="billing_first_name"]',
  'input[name="shipping_first_name"]',
].join(",");

const LAST_NAME_SELECTOR = [
  'input[name="last_name"]',
  'input[name="last-name"]',
  'input[name="lastname"]',
  'input[name*="lname"]',
  'input[name="family-name"]',
  'input[name*="surname"]',
  'input[autocomplete="family-name"]',
  // Dutch forms
  'input[name*="achternaam"]',
  // WooCommerce
  'input[name="billing_last_name"]',
  'input[name="shipping_last_name"]',
].join(",");

// Words that disqualify an input from being a "name" field (EN + NL)
const NAME_EXCLUDE_WORDS = ["user", "email", "company", "business", "url", "website", "domain", "search", "password", "hidden", "gebruiker", "bedrijf", "achternaam", "voornaam"];

// ── Helpers ────────────────────────────────────────────────────────

/** Walk up from click target to find a submit-like button. */
function findSubmitButton(target: Element): Element | null {
  let el: Element | null = target;
  while (el && el !== document.documentElement) {
    try {
      if (el.matches(SUBMIT_SELECTOR)) return el;
    } catch { /* invalid selector edge case */ }
    el = el.parentElement;
  }
  return null;
}

/** Find the parent form or form-like container (e.g. Elementor widget). */
function findFormContainer(button: Element): Element | null {
  const form = button.closest("form");
  if (form) return form;

  // Elementor form widget (no <form> element in some configurations)
  const elementorWidget = button.closest(".elementor-widget-form");
  if (elementorWidget) return elementorWidget;

  // HubSpot embedded form
  const hsForm = button.closest(".hs-form-private, .hbspt-form");
  if (hsForm) return hsForm;

  return null;
}

/** Find a name input within a container using CSS selectors. */
function findNameInput(container: Element, selector: string): Element | null {
  try {
    return container.querySelector(selector);
  } catch {
    return null;
  }
}

/**
 * Find a single "full name" input by scanning placeholder text.
 * Catches Elementor/WPForms/Gravity fields with random IDs but descriptive placeholders.
 */
function findFullNameInput(container: Element): Element | null {
  try {
    const inputs = container.querySelectorAll('input[type="text"]');
    for (let i = 0; i < inputs.length; i++) {
      const input = inputs[i] as HTMLInputElement;
      const placeholder = (input.placeholder || "").toLowerCase();
      const name = (input.name || "").toLowerCase();
      // Check BOTH signals: a Dutch placeholder ("Je naam") next to an
      // English name attribute (or vice versa) must still match
      const hint = placeholder + " " + name;
      if (!hint.trim()) continue;

      // Must contain "name" or Dutch "naam", without disqualifying words
      if (!hint.includes("name") && !hint.includes("naam")) continue;
      if (NAME_EXCLUDE_WORDS.some((w) => hint.includes(w))) continue;

      // Skip if it's hidden (honeypot fields)
      if (input.offsetParent === null && input.style.display === "none") continue;

      return input;
    }
  } catch { /* ignore */ }
  return null;
}

/**
 * Capture first/last name from a form container.
 * Strategy:
 * 1. Try explicit first_name + last_name selectors
 * 2. Fallback: find single "name" field, split on first space → fn + ln
 */
function captureNames(container: Element, promises: Promise<void>[]): void {
  const fnEl = findNameInput(container, FIRST_NAME_SELECTOR);
  const fnValue = getInputValue(fnEl);

  if (fnValue) {
    // Explicit first/last name fields found
    const lnValue = getInputValue(findNameInput(container, LAST_NAME_SELECTOR));
    promises.push(captureNamePair(fnValue, lnValue || null));
    return;
  }

  // Fallback: single name field (Elementor, CF7 your-name, etc.)
  const fullNameEl = findFullNameInput(container);
  const fullName = getInputValue(fullNameEl).trim();
  if (!fullName) return;

  promises.push(captureFullName(fullName));
}

/**
 * Hash whatever identity the form holds and hand it to the pixels.
 *
 * Deliberately separate from reporting a conversion. Learning who somebody is
 * costs nothing and helps every later event match, so it happens on the click.
 * Whether anything was actually SUBMITTED is a different question, answered by
 * form-submission-outcome.ts, and the two must not be decided together: that
 * coupling is what made a failed validation look like a lead.
 */
function captureIdentity(emailValue: string, phoneValue: string, container?: Element): Promise<boolean> {
  const promises: Promise<void>[] = [];

  if (emailValue && emailValue.includes("@")) {
    promises.push(captureEmail(emailValue));
  }

  if (phoneValue) {
    promises.push(capturePhone(phoneValue));
  }

  // Capture names from the form container
  if (container) {
    captureNames(container, promises);
  }

  if (promises.length === 0) return Promise.resolve(false);

  return Promise.all(promises).then(() => {
    persistUserData();
    reinitPixelUserData();
    refreshPlatformPixelUserData();
    return true;
  });
}

/** Report the submission that we have now established really happened. */
function sendSubmission(): void {
  const rule = findFormSubmitRule();
  if (!rule) {
    sendEvent({ source: "auto_capture" });
    return;
  }

  // once-per-visitor was honoured on the native submit path and not here, so
  // an AJAX form reported the same conversion on every submission. The pixel
  // fires a fresh event_id each time and the platform counts each one.
  // Marking the rule as fired happens inside sendEvent, at the moment the
  // event actually leaves (a consent-parked event must not burn the rule).
  if (shouldSkipForRule(rule.id, rule.oncePerVisitor)) {
    debugLog("ajaxFormDetect", "skipped: rule already fired for this visitor");
    return;
  }

  sendEvent({
    eventName: rule.eventName,
    eventValue: rule.eventValue,
    currency: rule.currency,
    trackingRuleId: rule.id,
  });
}

/**
 * Is a field on this form in a state that will stop the submission?
 *
 * `:invalid` rather than checkValidity(), because checkValidity() fires invalid
 * events on the page's own controls and we are only reading.
 *
 * The `novalidate` attribute is deliberately NOT treated as an exemption, and
 * that cost a round to learn. Contact Form 7 puts `novalidate` on every form it
 * renders and then validates in its own JavaScript, calling setCustomValidity
 * and refusing to submit. So the form says constraint validation is off while
 * something is enforcing it anyway: the first version of this check believed
 * the attribute, concluded nothing would block, and counted the lead exactly as
 * before.
 *
 * What `:invalid` really means is that the page itself has marked a field
 * unacceptable, by a constraint attribute or by setCustomValidity. Whether the
 * browser or the framework acts on it, that is not a submission.
 *
 * A button carrying `formnovalidate` IS an exemption: that is an explicit
 * "submit this without checking", and such a submission does go through.
 */
function willBeBlockedByValidation(container: Element, button: Element): boolean {
  if (container.tagName !== "FORM") return false;
  if ((button as HTMLButtonElement).formNoValidate) return false;
  try {
    return !!container.querySelector(":invalid");
  } catch {
    return false;
  }
}

// ── Layer 1: Submit button click detection ─────────────────────────

function handleSubmitButtonClick(e: MouseEvent): void {
  const target = e.target as Element;
  if (!target) return;

  const button = findSubmitButton(target);
  if (!button) return;

  const container = findFormContainer(button);
  if (!container) return;
  // Same guard as the submit path: a shop's add-to-cart and search controls
  // are forms too, and a Lead rule without a page filter would count them.
  if (isNonConversionForm(container)) return;

  // Read email/phone synchronously BEFORE any AJAX handler clears the form
  const emailValue = getInputValue(findEmailInput(container));
  const phoneValue = getInputValue(findPhoneInput(container));

  if (!emailValue && !phoneValue) return;

  debugLog("ajaxFormDetect", "submit button clicked, email=" + !!emailValue, "phone=" + !!phoneValue);

  const blocked = willBeBlockedByValidation(container, button);

  // Identity is captured either way. The visitor typed their address; that is
  // true whether or not this particular attempt goes through, and it makes the
  // next event match better.
  const identity = captureIdentity(emailValue, phoneValue, container);

  if (blocked) {
    debugLog("ajaxFormDetect", "not counted — the browser will refuse this submission");
    return;
  }

  // Deferred so the native submit event fires first. A form that fires `submit`
  // is handled by dom-listeners, which arms the same wait; arming a second one
  // here would report the conversion twice.
  setTimeout(() => {
    if (wasNativelySubmitted(container) || hasPendingSubmission()) {
      debugLog("ajaxFormDetect", "skipped — the submit path already has this one");
      return;
    }

    // No submit event at all, which is what a form built entirely in
    // JavaScript looks like. Hold the conversion until its framework reports.
    //
    // Armed BEFORE the hashing finishes, for the reason spelled out on the
    // submit path: Contact Form 7 answers in about 170ms, and arming after the
    // hashes resolve lets that verdict arrive while nothing is waiting for it.
    // It is then discarded and the conversion is counted by the timeout anyway,
    // which is exactly the bug the hold exists to prevent.
    armPendingSubmission(() => identity.then(sendSubmission));
  }, 0);
}

// ── Layer 2: Input blur monitoring ─────────────────────────────────

function handleInputBlur(e: Event): void {
  const target = e.target as Element;
  if (!target || target.tagName !== "INPUT") return;

  let isEmail = false;
  let isPhone = false;
  try {
    isEmail = target.matches(EMAIL_INPUT_SELECTOR);
    if (!isEmail) isPhone = target.matches(PHONE_INPUT_SELECTOR);
  } catch { return; }

  if (!isEmail && !isPhone) return;

  const value = getInputValue(target);
  if (!value) return;
  if (isEmail && !value.includes("@")) return;

  debugLog("ajaxFormDetect", "blur captured", isEmail ? "email" : "phone");

  const capture = isEmail ? captureEmail(value) : capturePhone(value);
  capture.then(() => {
    persistUserData();
    reinitPixelUserData();
    refreshPlatformPixelUserData();
  });
}

// ── Public API ─────────────────────────────────────────────────────

export function installAjaxFormDetection(): void {
  // Layer 1: submit button clicks (capture phase, works for dynamic popups)
  document.addEventListener("click", handleSubmitButtonClick, true);

  // Layer 1b: what the form framework says came of it, for both submit paths
  installSubmissionSignalListeners();

  // Layer 2: email/phone input blur + change (capture phase)
  // blur: user leaves the field. change: browser autofill.
  document.addEventListener("blur", handleInputBlur, true);
  document.addEventListener("change", handleInputBlur, true);

  debugLog("ajaxFormDetect", "installed");
}
